import * as React from "react";
import { ArrowDown, ArrowRight, ArrowUp, BookOpen, BookOpenText } from "lucide-react";
import { cn } from "../utils/cn";
import { Progress } from "../primitives/progress";
import { Text, Mono, Heading } from "../primitives/typography";
import type { ProgressColor } from "../primitives/progress";

export type ContentTrend = "up" | "down" | "flat";

export interface ContentAssignmentItem {
    id: string;
    name: string;
    lessonCount: number;
    orgCount: number;
    coveragePct: number;
    trend: ContentTrend;
}

export interface ContentAssignmentCardProps extends React.HTMLAttributes<HTMLDivElement> {
    subtitle?: string;
    items: ContentAssignmentItem[];
    onManageClick?: () => void;
}

function getCoverageColor(pct: number): ProgressColor {
    if (pct >= 80) return "success";
    if (pct >= 50) return "default";
    if (pct >= 25) return "warning";
    return "destructive";
}

const TrendIcon = ({ trend }: { trend: ContentTrend }) => {
    if (trend === "up") return <ArrowUp className="text-success h-4 w-4" />;
    if (trend === "down") return <ArrowDown className="text-destructive h-4 w-4" />;
    return <ArrowRight className="text-muted-foreground h-4 w-4" />;
};

const ContentAssignmentCard = React.forwardRef<HTMLDivElement, ContentAssignmentCardProps>(
    ({ className, subtitle, items, onManageClick, ...props }, ref) => (
        <div ref={ref} className={cn("border-border bg-card flex flex-col rounded-2xl border", className)} {...props}>
            {/* Header */}
            <div className="flex items-start justify-between px-5 pt-5 pb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <BookOpenText className="text-warning h-4 w-4 shrink-0" />
                        <Heading level={6} className="font-semibold">
                            Content Assignment
                        </Heading>
                    </div>
                    {subtitle && (
                        <Text size="sm" muted>
                            {subtitle}
                        </Text>
                    )}
                </div>
                {onManageClick && (
                    <button
                        onClick={onManageClick}
                        className="text-secondary hover:text-secondary/80 text-sm font-medium whitespace-nowrap transition-colors"
                    >
                        Manage content →
                    </button>
                )}
            </div>

            {/* Rows */}
            <div className="divide-border flex flex-col divide-y">
                {items.map((item) => {
                    const color = getCoverageColor(item.coveragePct);
                    return (
                        <div key={item.id} className="flex items-center gap-3 px-5 py-4">
                            <div className="bg-muted text-muted-foreground flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                                <BookOpen className="h-4 w-4" />
                            </div>

                            <div className="min-w-0 flex-1">
                                <Text size="sm" weight="semibold" className="font-display text-foreground truncate">
                                    {item.name}
                                </Text>
                                <Text size="xs" muted>
                                    {item.lessonCount} lessons
                                </Text>
                            </div>

                            <div className="flex shrink-0 flex-col items-end gap-0.5">
                                <Mono size="xs" className="text-foreground font-semibold">
                                    {item.orgCount}
                                </Mono>
                                <Text size="xs" muted>
                                    orgs
                                </Text>
                            </div>

                            <div className="flex w-32 shrink-0 flex-col gap-1 sm:w-40">
                                <div className="flex items-center justify-between">
                                    <Mono size="xs" className="text-foreground font-semibold">
                                        {item.coveragePct}%
                                    </Mono>
                                    <Mono size="xs" muted>
                                        coverage
                                    </Mono>
                                </div>
                                <Progress value={item.coveragePct} color={color} className="h-1.5" />
                            </div>
                            <TrendIcon trend={item.trend} />
                        </div>
                    );
                })}
            </div>
        </div>
    ),
);
ContentAssignmentCard.displayName = "ContentAssignmentCard";

export { ContentAssignmentCard, getCoverageColor };
